import {
  WARRANTY_KEYS,
  missingComplianceFields,
  telLink,
  type Project,
  type Unit,
  type Warranties,
} from '@alrimal/contracts';
import { formatYears, translate, type Locale } from '../i18n.ts';

/**
 * The indexable shell (§7).
 *
 * Rendered to static HTML at build time and shipped inside `index.html`, so:
 *
 * · a crawler sees the whole project — every room of every level — with no JS;
 * · a WhatsApp link preview resolves, which is how this spreads at all;
 * · the first paint is real content, not a canvas that has not loaded yet;
 * · a device with no WebGL 2 still gets everything except the model.
 *
 * It takes no context and no hooks. It has to render identically in Node at
 * build time and in the browser as the fallback.
 */

export interface SeoShellProps {
  project: Project;
  units: readonly Unit[];
  warranties: Warranties;
  locale?: Locale;
}

export function SeoShell({
  project,
  units,
  warranties,
  locale = 'ar',
}: SeoShellProps) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const missing = missingComplianceFields(project);

  return (
    <div className="seo-shell" id="seo-shell">
      <header>
        <h1>{project.nameAr}</h1>
        {project.demoData ? (
          <p className="notice">{t('notice.demo')}</p>
        ) : null}
        <p className="seo-shell__lede">{project.seo.descriptionAr}</p>
        <p className="seo-shell__meta">
          <span>{project.developerAr}</span>
          <span>
            {t('brand.marketedBy')} {project.marketerAr}
          </span>
          <span>
            {project.districtAr} — {project.cityAr}
          </span>
        </p>
      </header>

      <section>
        <h2>{t('highlights.title')}</h2>
        <ul>
          {project.highlightsAr.map((highlight) => (
            <li key={highlight}>{highlight}</li>
          ))}
        </ul>
      </section>

      <section>
        <h2>{t('location.title')}</h2>
        <ul>
          {project.locationNotesAr.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </section>

      <section>
        <h2>{t('level.title')}</h2>
        <div className="seo-shell__grid">
          {units.flatMap((unit) =>
            unit.dwellings.map((dwelling) => (
              <article className="seo-shell__card" key={dwelling.id}>
                <h3>
                  {dwelling.labelAr} — {unit.id}
                </h3>
                <p className="seo-shell__meta">
                  {unit.area !== undefined ? (
                    <span>
                      {t('fact.area')}: {unit.area} {t('fact.squareMetres')}
                      {unit.areaBasis === 'TBC'
                        ? ` (${t('fact.areaBasisTbc')})`
                        : ''}
                    </span>
                  ) : null}
                  <span>
                    {t('fact.street')}: {unit.streetWidth} {t('fact.metres')}
                  </span>
                  <span>
                    {t('fact.orientation')}: {unit.orientationAr}
                  </span>
                  <span>
                    {t(`status.${dwelling.status}`)}
                  </span>
                </p>
                {dwelling.levels.map((level) => (
                  <div key={level.code}>
                    <h4>{level.nameAr}</h4>
                    <ul>
                      {level.roomsAr.map((room) => (
                        <li key={room}>{room}</li>
                      ))}
                      {level.bathrooms !== null ? (
                        <li>
                          {t('fact.bathrooms')}: {level.bathrooms}
                        </li>
                      ) : null}
                    </ul>
                  </div>
                ))}
              </article>
            )),
          )}
        </div>
      </section>

      <section>
        <h2>{t('warranties.title')}</h2>
        <ul>
          {WARRANTY_KEYS.flatMap((key) =>
            (warranties[key] ?? []).map((item) => (
              <li key={`${key}-${item.labelAr}`}>
                {item.labelAr} — {formatYears(locale, item.years)}
                {item.noteAr ? ` (${item.noteAr})` : ''}
              </li>
            )),
          )}
        </ul>
      </section>

      <section>
        <h2>{t('form.title')}</h2>
        <p>
          <a href={telLink(project.contact.phone)} className="num">
            {project.contact.phone}
          </a>
          {' · '}
          <span className="ltr">{project.contact.social}</span>
        </p>
        {missing.length > 0 ? (
          <p className="notice">{t('notice.licenceMissing')}</p>
        ) : (
          <p className="seo-shell__meta">
            <span className="num">{project.advertisingLicence}</span>
            <span className="num">{project.wafiRegistration}</span>
          </p>
        )}
      </section>
    </div>
  );
}
